import {
  memo,
  useEffect,
  useState,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PaperSendIcon from "@assets/svg/PaperSendIcon";
import { MESSAGE_EVENTS } from "@custom-hooks/useChat";
import PaperClipIcon from "@assets/svg/PaperClipIcon";
import { ISelectedContact } from "../MessagesPage";
import { uploadChatMedia } from "@endpoints/images.service";
import { useSnackbar } from "notistack";
import { useSocket } from "context/SocketProvider";
import { IUser } from "@type/user.type";
import { v4 as uuidv4 } from "uuid";

interface IMessageInputBarProps {
  isTyping: boolean;
  selectedContact: ISelectedContact;
  sendMessage: ({
    message,
    setMessage,
    attachments,
    setAttachments,
  }: {
    message: any;
    setMessage: any;
    attachments?: any[];
    setAttachments?: any;
  }) => Promise<void>;
  appendLocalMessage: (message: any) => void;
  triggerTyping: (status: any) => void;
  user: IUser;
}

const MessageInputBar = ({
  isTyping,
  selectedContact,
  sendMessage,
  appendLocalMessage,
  triggerTyping,
  user,
}: IMessageInputBarProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCurrentlyTypingRef = useRef(false);
  const socketRef = useSocket();
  const socket = socketRef?.current;

  const { enqueueSnackbar } = useSnackbar();

  // Local state
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<
    {
      id: string;
      file: File;
      previewUrl?: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const clearAttachments = () => {
    setAttachments((prev) => {
      prev.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const nextItems = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      file,
      previewUrl: file.type?.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
    }));

    setAttachments((prev) => [...prev, ...nextItems]);
    event.target.value = "";
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const uploadSelectedAttachments = async () => {
    if (!attachments.length) return [];

    // uuid string
    const chatId = uuidv4();

    const recipientUserId =
      selectedContact?.participantDetails?.userId ||
      selectedContact?.participantDetails?._id ||
      "";

    const formData = new FormData();
    attachments.forEach((item) => {
      formData.append("files", item.file);
    });

    formData.append("jobId", selectedContact?.jobId || "");
    formData.append("recipientId", recipientUserId);
    formData.append("senderId", user?._id || user?.userId || "");
    formData.append("chatId", chatId || "");
    formData.append("clientMsgId", chatId || "");
    formData.append("message", message || "");

    const mediaUploadResponse = await uploadChatMedia(formData);
    const uploadInfo = mediaUploadResponse?.data;

    if (!uploadInfo?.message) {
      throw new Error("Failed to create chat media message");
    }

    socket?.emit(MESSAGE_EVENTS.REFRESH_MESSAGES, {
      jobId: selectedContact?.jobId || "",
      recipientId: recipientUserId,
    });

    return uploadInfo;
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    } else {
      if (!isCurrentlyTypingRef.current) {
        triggerTyping(true);
        isCurrentlyTypingRef.current = true;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        triggerTyping(false);
        isCurrentlyTypingRef.current = false;
      }, 2000);
    }
  };

  const handleSendClick = async () => {
    if (
      isLoading ||
      (!message.trim() && attachments.length === 0) ||
      !selectedContact
    )
      return;

    try {
      setIsLoading(true);
      if (attachments.length) {
        const uploadResult = await uploadSelectedAttachments();
        appendLocalMessage(uploadResult.message);
        setMessage("");
        clearAttachments();
      } else {
        await sendMessage({
          message,
          setMessage,
        });
      }

      triggerTyping(false);
      isCurrentlyTypingRef.current = false;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Failed to send message", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      attachments.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, [attachments]);

  return (
    <div className="flex flex-col px-1.5 pb-1.5 pt-0">
      <div className="h-6 flex items-center px-2">
        {isTyping && (
          <span className="text-xs text-gray-500 italic">Typing...</span>
        )}
      </div>
      {attachments.length ? (
        <div className="flex flex-wrap gap-2 px-2 pb-2">
          {attachments.map((item) => (
            <div
              key={item.id}
              className="relative flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 pr-8 max-w-[220px]"
            >
              {item.previewUrl ? (
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="h-10 w-10 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded bg-white border border-gray-200 flex items-center justify-center text-[10px] font-semibold text-light-black uppercase flex-shrink-0">
                  {item.file.name.split(".").pop()?.slice(0, 4)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-charcoal truncate">
                  {item.file.name}
                </p>
                <p className="text-[10px] text-light-black">
                  {(item.file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeAttachment(item.id)}
                className="absolute right-1 top-1 text-[10px] font-bold text-light-black hover:text-charcoal"
                aria-label={`Remove ${item.file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex gap-4 rounded-md border p-1.5 bg-white">
        <textarea
          className="px-3 py-2 w-full bg-white rounded text-xs font-medium focus:border-gray-300 focus:outline-none"
          rows={1}
          placeholder="Type your message here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
        ></textarea>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept="image/*,application/pdf,.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx"
          onChange={handleAttachmentChange}
        />
        <button
          className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md disabled:cursor-not-allowed"
          onClick={handleSendClick}
          disabled={
            isLoading ||
            (!message.trim() && attachments.length === 0) ||
            !selectedContact
          }
        >
          {isLoading ? (
            <LoadingAnimation color="black" className={"w-4 h-4"} />
          ) : (
            <PaperSendIcon />
          )}
        </button>
        <button
          type="button"
          className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md"
          onClick={handleAttachmentClick}
        >
          <PaperClipIcon />
        </button>
      </div>
    </div>
  );
};

export default memo(MessageInputBar);
